/**
 * Acceso del proveedor desde el admin (CC-03, SP-251): restablecer la contraseña
 * y cambiar el correo de acceso. Las reglas son las del back (SP-250): la
 * contraseña pide 8 caracteres, una mayúscula y un número, igual que el alta.
 */

export const LARGO_MINIMO_DE_CONTRASENA = 8

export const reglasDeContrasena = (contrasena: string) => ({
  largo: contrasena.length >= LARGO_MINIMO_DE_CONTRASENA,
  mayuscula: /[A-Z]/.test(contrasena),
  numero: /\d/.test(contrasena),
})

/** Mensaje de la primera regla que falla, o null si la contraseña vale. */
export const errorDeContrasena = (contrasena: string): string | null => {
  const reglas = reglasDeContrasena(contrasena)
  if (!reglas.largo) return `Debe tener al menos ${LARGO_MINIMO_DE_CONTRASENA} caracteres`
  if (!reglas.mayuscula) return 'Debe incluir una mayúscula'
  if (!reglas.numero) return 'Debe incluir un número'
  return null
}

const CORREO = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** El correo nuevo se compara sin importar mayúsculas ni espacios. */
export const normalizarCorreo = (correo: string): string => correo.trim().toLowerCase()

export const errorDeCorreo = (correo: string, actual?: string | null): string | null => {
  const nuevo = normalizarCorreo(correo)
  if (!nuevo) return 'Escribe el correo nuevo'
  if (!CORREO.test(nuevo)) return 'El correo no es válido'
  if (actual && nuevo === normalizarCorreo(actual)) return 'El correo nuevo es igual al actual'
  return null
}

const MAYUSCULAS = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
const MINUSCULAS = 'abcdefghijkmnopqrstuvwxyz'
const NUMEROS = '23456789'
const TODOS = MAYUSCULAS + MINUSCULAS + NUMEROS

/** Entero aleatorio en [0, tope) con el generador criptográfico del navegador. */
const aleatorio = (tope: number): number => {
  const buffer = new Uint32Array(1)
  crypto.getRandomValues(buffer)
  return buffer[0] % tope
}

/**
 * Contraseña de 12 caracteres que cumple las reglas. Deja fuera los caracteres
 * que se confunden al dictarlos (0/O, 1/l/I) porque el admin se la pasa al
 * proveedor por fuera del sistema.
 */
export const contrasenaGenerada = (): string => {
  const caracteres = [
    MAYUSCULAS[aleatorio(MAYUSCULAS.length)],
    MINUSCULAS[aleatorio(MINUSCULAS.length)],
    NUMEROS[aleatorio(NUMEROS.length)],
  ]
  while (caracteres.length < 12) caracteres.push(TODOS[aleatorio(TODOS.length)])
  // Fisher-Yates: sin esto las tres primeras posiciones serían siempre las mismas.
  for (let i = caracteres.length - 1; i > 0; i--) {
    const j = aleatorio(i + 1)
    ;[caracteres[i], caracteres[j]] = [caracteres[j], caracteres[i]]
  }
  return caracteres.join('')
}

/** Los errores de validación de NestJS traen `message` como arreglo. */
export const mensajeDeError = (err: unknown, respaldo: string): string => {
  const mensaje = (err as { message?: unknown } | null)?.message
  if (Array.isArray(mensaje)) return mensaje.filter(Boolean).join(' ') || respaldo
  return typeof mensaje === 'string' && mensaje.trim() ? mensaje : respaldo
}
